import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  autogrow?: boolean;
}

const textareaVariants = cva(
  'focus-brand-shadow flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: '',
        borderless: 'border-0 focus-visible:border focus-visible:border-input',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, autogrow = false, variant, onInput, ...props }, ref) => {
    const [textareaElement, setTextareaElement] =
      React.useState<HTMLTextAreaElement | null>(null);

    const adjustHeight = React.useCallback(() => {
      if (textareaElement && autogrow) {
        textareaElement.style.height = 'auto';
        textareaElement.style.height = `${textareaElement.scrollHeight}px`;
      }
    }, [autogrow, textareaElement]);

    React.useEffect(() => {
      adjustHeight();
    }, [adjustHeight, props.value]);

    // Adjust height when textarea element is set
    React.useEffect(() => {
      if (textareaElement && autogrow) {
        adjustHeight();
      }
    }, [textareaElement, autogrow, adjustHeight]);

    const handleInput = React.useCallback(
      (e: React.FormEvent<HTMLTextAreaElement>) => {
        adjustHeight();
        onInput?.(e);
      },
      [adjustHeight, onInput]
    );

    return (
      <textarea
        className={cn(textareaVariants({ variant }), className)}
        ref={node => {
          setTextareaElement(node);
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        onInput={handleInput}
        {...props}
      />
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
